import { lazy } from "react";
import { createBrowserRouter, Navigate, useLocation, useParams } from "react-router-dom";
import ClientAccountSettingsPage from "@/features/accountSettings/components/ClientAccountSettingsPage";
import ClientEraseDataPage from "@/features/accountSettings/components/ClientEraseDataPage";
import { ROUTES } from "@/app/constants";
import { AppLayout } from "@/shared/layout/AppLayout";
import { AuthLayout } from "@/shared/layout/AuthLayout";
import { NotFoundPage } from "@/shared/layout/NotFoundPage";
import { PendingApprovalPage } from "@/shared/layout/PendingApprovalPage";
import AuthGuard from "./AuthGuard";
import VerifyPhonePage from "@/features/auth/components/VerifyPhonePage";
import { GuestGuard } from "./GuestGuard";
import { RoleGuard } from "./RoleGuard";
import { PortalGuard } from "./PortalGuard";
import { ObjectGuard } from "./ObjectGuard";
import { RoleHomeRedirect } from "./RoleHomeRedirect";

const LoginPage = lazy(() =>
  import("@/features/auth").then((module) => ({ default: module.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("@/features/auth").then((module) => ({ default: module.RegisterPage })),
);
const ConfirmEmailChangePage = lazy(() =>
  import("@/features/auth/components/ConfirmEmailChangePage").then((module) => ({ default: module.default })),
);
const HomePage = lazy(() => import("@/features/home/components/HomePage"));
const PackagesPage = lazy(() =>
  import("@/features/packages").then((module) => ({ default: module.default })),
);
const PricingPage = lazy(() =>
  import("@/features/pricings").then((module) => ({ default: module.default })),
);
const ServicesPage = lazy(() =>
  import("@/features/services").then((module) => ({ default: module.default })),
);
const ItemsPage = lazy(() =>
  import("@/features/Items").then((module) => ({ default: module.default })),
);
const EventPage = lazy(() =>
  import("@/features/event-mode").then((module) => ({ default: module.default })),
);
const LeadHomePage = lazy(() =>
  import("@/features/lead_home").then((module) => ({ default: module.default })),
);
const VideoPage = lazy(() =>
  import("@/features/Videoassesment").then((module) => ({ default: module.default })),
);
const LovPage = lazy(() =>
  import("@/features/LOV").then((module) => ({ default: module.default })),
);
const LovValuesPage = lazy(() =>
  import("@/features/LOV/components/LovValuesPage"),
);
const LovFormPage = lazy(() =>
  import("@/features/LOV/components/LovFormPage"),
);
const PackagePaymentPage = lazy(() =>
  import("@/features/packageApproval").then((module) => ({ default: module.default })),
);
const SettingsPage = lazy(() =>
  import("@/features/settings/components/SettingsPage"),
);
// Admin → Payments: money received (the existing screen) plus the payables
// tabs (unified requirements D5) — one page, no new menu.
const PaymentsPage = lazy(() => import("@/features/payables/components/PaymentsHub"));
const ComposerPage = lazy(() =>
  import("@/features/composer").then((module) => ({ default: module.default })),
);
const RemedyCatalogPage = lazy(() =>
  import("@/features/remedyCatalog").then((module) => ({ default: module.default })),
);
const ReportsPage = lazy(() =>
  import("@/features/reports").then((module) => ({ default: module.default })),
);
// Access Control (RBAC v2) — replaces the old Role Permissions screen, which
// only ever exposed the role x permission grid. This console also covers the
// screen registry, user assignment, the simulator and the change log.
const AccessControlPage = lazy(() =>
  import("@/features/accessAdmin").then((module) => ({ default: module.default })),
);
const JobHealthPage = lazy(() =>
  import("@/features/jobHealth/components/JobHealthPage"),
);
const AuditLogPage = lazy(() =>
  import("@/features/auditLog/components/AuditLogPage"),
);
const PreSalesLeadsPage = lazy(() =>
  import("@/features/preSales/leads/components/PreSalesLeadsPage"),
);
const CreateLeadPage = lazy(() =>
  import("@/features/preSales/leads/components/CreateLeadPage"),
);
const PreSalesPipelinePage = lazy(() =>
  import("@/features/preSales/leads/components/PreSalesPipelinePage"),
);
const StaffDirectoryPage = lazy(() => import("@/features/hr/components/StaffDirectoryPage"));
const EmployeeProfilePage = lazy(() => import("@/features/hr/components/EmployeeProfilePage"));
const HrSettingsPage = lazy(() => import("@/features/hr/components/HrSettingsPage"));
const HiringPage = lazy(() => import("@/features/hr/components/HiringPage"));
const OnboardingPage = lazy(() => import("@/features/hr/components/OnboardingPage"));
const AttendancePage = lazy(() => import("@/features/hr/components/AttendancePage"));
const ShiftsPage = lazy(() => import("@/features/hr/components/ShiftsPage"));
const ApprovalsPage = lazy(() => import("@/features/hr/components/ApprovalsPage"));
const LeavePage = lazy(() => import("@/features/hr/components/LeavePage"));
const ReconciliationPage = lazy(() => import("@/features/hr/components/ReconciliationPage"));
const PayrollPage = lazy(() => import("@/features/hr/components/PayrollPage"));
const PulsePage = lazy(() => import("@/features/hr/components/PulsePage"));
const HrCalendarPage = lazy(() => import("@/features/hr/components/HrCalendarPage"));
const HrSeparationPage = lazy(() => import("@/features/hr/components/HrSeparationPage"));
const TaskAttachmentsPage = lazy(() => import("@/features/hr/components/TaskAttachmentsPage"));
const RenewalsPage = lazy(() => import("@/features/dashboards/components/RenewalsPage"));
const NewInterestsPage = lazy(() => import("@/features/clientRelationship/NewInterestsPage"));
const BizCardsPage = lazy(() => import("@/features/bizcard/BizCardsPage"));
const CommunityAdminPage = lazy(() => import("@/features/community/CommunityAdminPage"));
const PerformancePage = lazy(() => import("@/features/dashboards/components/PerformancePage"));
const TasksPage = lazy(() => import("@/features/tasks/components/TasksPage"));
const TaskTypesPage = lazy(() => import("@/features/tasks/components/TaskTypesPage"));
const PreSalesProposalsPage = lazy(() =>
  import("@/features/preSales/proposals/components/PreSalesProposalsPage"),
);
const WhatsAppGroupsPage = lazy(() =>
  import("@/features/preSales/whatsapp/components/WhatsAppGroupsPage"),
);
const IntakeReviewPage = lazy(() =>
  import("@/features/intake/components/IntakeReviewPage"),
);
const PreSalesReferralsPage = lazy(() =>
  import("@/features/referrals/PreSalesReferralsPage"),
);
const PostSalesClientsPage = lazy(() =>
  import("@/features/postSales/clients/components/PostSalesClientsPage"),
);
const CreateClientPage = lazy(() =>
  import("@/features/postSales/clients/components/CreateClientPage"),
);
const ProjectDetailPage = lazy(() =>
  import("@/features/postSales/delivery/components/ProjectDetailPage"),
);
const PostSalesDeliveryPage = lazy(() =>
  import("@/features/postSales/delivery/components/PostSalesDeliveryPage"),
);
const PostSalesClientProfilePage = lazy(() =>
  import("@/features/postSales/clientProfile/components/PostSalesClientProfilePage"),
);
const PostSalesCompaniesPage = lazy(() =>
  import("@/features/postSales/companies/components/PostSalesCompaniesPage"),
);
const CompanyWorkspacePage = lazy(() =>
  import("@/features/postSales/companies/components/CompanyWorkspacePage"),
);
const ClientDeepDivePage = lazy(() =>
  import("@/features/postSales/clients/components/ClientDeepDivePage"),
);
const ClientPortalPage = lazy(() =>
  import("@/features/clientPortal").then((module) => ({ default: module.default })),
);
const ClientPortalReportPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalReportPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalProfilePage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalProfilePage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalCommunityPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalCommunityPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalBizCalendarPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalBizCalendarPage").then((module) => ({
    default: module.default,
  })),
);
const BizCalendarReviewPage = lazy(() =>
  import("@/features/bizcalendarReview/components/BizCalendarReviewPage").then((module) => ({
    default: module.default,
  })),
);
const BizCalendarReviewDetailPage = lazy(() =>
  import("@/features/bizcalendarReview/components/BizCalendarReviewDetailPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalRemediesPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalRemediesPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalDashaPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalDashaPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalAstroUpdatesPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalAstroUpdatesPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalPaymentPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalPaymentPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalLayout = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalLayout").then((module) => ({
    default: module.ClientPortalLayout,
  })),
);
// Refer & Earn + Numerology Center + Report Limits (our additions).
const ClientPortalReferAndEarnPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalReferAndEarnPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalLedgerPage = lazy(() => import("@/features/payables/components/ClientPortalLedgerPage"));
const ClientPortalProjectsPage = lazy(() => import("@/features/clientPortal/projects/ClientPortalProjectsPage"));
const ClientPortalBizCardPage = lazy(() => import("@/features/bizcard/ClientPortalBizCardPage"));
const ClientPortalCommunityApplyPage = lazy(() => import("@/features/community/ClientPortalCommunityApplyPage"));
const ClientPortalChapterPage = lazy(() => import("@/features/community/ClientPortalChapterPage"));
const ClientPortalMeetingsPage = lazy(() => import("@/features/community/ClientPortalMeetingsPage"));
const ClientPortalFeedPage = lazy(() => import("@/features/community/ClientPortalFeedPage"));
const ClientPortalBoardPage = lazy(() => import("@/features/community/ClientPortalBoardPage"));
const ClientPortalWorkshopsPage = lazy(() => import("@/features/community/ClientPortalWorkshopsPage"));
const WorkshopCertificatePage = lazy(() => import("@/features/community/WorkshopCertificatePage"));
const ClientPortalAnalyticsPage = lazy(() => import("@/features/community/ClientPortalAnalyticsPage"));
const ClientPortalBusinessPage = lazy(() => import("@/features/community/ClientPortalBusinessPage"));
const CalendarOAuthReturnPage = lazy(() => import("@/features/calendarSync/CalendarOAuthReturnPage"));
const ClientPortalPlansPage = lazy(() =>
  import("@/features/clientPortal/plans/ClientPortalPlansPage").then((module) => ({ default: module.default })),
);
const ClientPortalPackagesPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalPackagesPage").then((module) => ({
    default: module.default,
  })),
);
const ClientPortalNumerologyCenterPage = lazy(() =>
  import("@/features/clientPortal/components/ClientPortalNumerologyCenterPage").then((module) => ({
    default: module.default,
  })),
);
const ReportLimitsPage = lazy(() =>
  import("@/features/reportLimits/ReportLimitsPage").then((module) => ({
    default: module.default,
  })),
);
const TeamSecurityOverviewPage = lazy(() =>
  import("@/features/teamSecurity/TeamSecurityOverviewPage"),
);
// The Astrology workspace — spec Development/Astrology §1.
const KundaliPage = lazy(() =>
  import("@/features/astrology/components/KundaliPage"),
);

const KundaliWorkbenchPage = lazy(() =>
  import("@/features/astrology/components/KundaliWorkbenchPage"),
);
const EventKundaliPage = lazy(() =>
  import("@/features/astrology/components/EventKundaliPage"),
);
const PublicAvailabilityPage = lazy(() =>
  import("@/features/hr/components/PublicAvailabilityPage"),
);
const WalkInConsentPage = lazy(() =>
  import("@/features/astrology/components/WalkInConsentPage"),
);
const EmailPreferencesPage = lazy(() =>
  import("@/features/emailPreferences/EmailPreferencesPage"),
);
const EmployeeIntakePage = lazy(() =>
  import("@/features/hr/components/EmployeeIntakePage"),
);
const BizCalendarsPage = lazy(() =>
  import("@/features/astrology/components/BizCalendarsPage"),
);
const AstrologyNumerologyPage = lazy(() =>
  import("@/features/astrology/components/NumerologyPage"),
);
const RulesReferencePage = lazy(() =>
  import("@/features/astrology/components/RulesReferencePage"),
);

/**
 * Old-path redirects into the Astrology workspace (spec §1.2 — "keep
 * 301-style redirects from the old paths for a release; people have them
 * bookmarked"). The query string is preserved because live flows depend on
 * it — e.g. the client page keeps its active tab and accountId in the URL.
 * (The old ?from=allocation&returnPath=… remedy links are retired — the
 * catalog form opens in an overlay on the client page since 2026-09-10 —
 * but preserving the search stays correct either way.)
 */
function RedirectPreservingSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}
function RedirectReviewDetail() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`${ROUTES.astrologyReview}/${id}${search}`} replace />;
}

const ForgotPasswordPage = lazy(() =>
  import("@/features/auth/components/ForgotPasswordPage").then((module) => ({
    default: module.default,
  })),
);
const SetPasswordPage = lazy(() =>
  import("@/features/auth/components/SetPasswordPage"),
);
const ResetPasswordPage = lazy(() =>
  import("@/features/auth/components/ResetPasswordPage").then((module) => ({
    default: module.default,
  })),
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RoleHomeRedirect />,
  },

  // Fully public — token-driven (may be opened while logged out).
  { path: ROUTES.confirmEmailChange, element: <ConfirmEmailChangePage /> },
  // The walk-in consent page (spec §5.3) — opened from a WhatsApp link by
  // somebody with no account. Public rather than guest-only: GuestGuard would
  // redirect a signed-in reader away from a link addressed to them.
  { path: ROUTES.walkInConsent,      element: <WalkInConsentPage /> },
  // The new hire's intake form (BP-ISS-20260919-076) — opened from a link HR
  // sends, by somebody with no login yet. Public for the same reason.
  { path: ROUTES.employeeIntake,     element: <EmployeeIntakePage /> },
  // The link in every notice email (decision #39) — opened from an inbox,
  // signed in or not. Public for the same reason.
  { path: ROUTES.emailPreferences,   element: <EmailPreferencesPage /> },
  // The public availability link (BA §2.5) — busy or free, never a title.
  // Public rather than guest-only, for the same reason as the line above.
  { path: ROUTES.publicAvailability, element: <PublicAvailabilityPage /> },

  // -------- Public / guest-only routes --------
  {
    element: <GuestGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: ROUTES.login,          element: <LoginPage /> },
          { path: ROUTES.register,       element: <RegisterPage /> },
          { path: ROUTES.event,          element: <EventPage /> },
          { path: ROUTES.video,          element: <VideoPage /> },
          { path: ROUTES.forgotPassword, element: <ForgotPasswordPage /> },
          { path: ROUTES.resetPassword,  element: <ResetPasswordPage /> },
          { path: ROUTES.setPassword,    element: <SetPasswordPage /> },
        ],
      },
    ],
  },

  // -------- Any authenticated user (regardless of role/status) --------
  {
    element: <AuthGuard />,
    children: [
      // Must sit INSIDE AuthGuard (it needs the session) but is the one
      // path AuthGuard lets an unverified client reach - otherwise the
      // redirect is a closed loop.
      { path: ROUTES.verifyPhone,     element: <VerifyPhonePage /> },
      { path: ROUTES.pendingApproval, element: <PendingApprovalPage /> },
      { path: ROUTES.lead_home,       element: <LeadHomePage /> },
      // Staff and members alike connect their own calendar (E14).
      { path: ROUTES.calendarOAuthReturn, element: <CalendarOAuthReturnPage /> },
    ],
  },

  // -------- Admin shell (ADMIN / SUPER_ADMIN / STAFF only) --------
  {
    element: <AuthGuard />,
    children: [
      {
        element: <RoleGuard allowedRoles={["ADMIN", "SUPER_ADMIN", "STAFF"]} />,
        children: [
          {
            element: <AppLayout />,
            children: [
              // One employee, one dashboard (owner, 2026-09-10). The four module
              // dashboards redirect here rather than 404 — bookmarks and the
              // old nav entries keep working.
              { path: ROUTES.home,                 element: <HomePage /> },
              { path: ROUTES.dashboard,            element: <RedirectPreservingSearch to={ROUTES.home} /> },
              { path: ROUTES.packages,             element: <PackagesPage /> },
              { path: ROUTES.Services,             element: <ServicesPage /> },
              { path: ROUTES.preSales,             element: <RedirectPreservingSearch to={ROUTES.home} /> },
              { path: ROUTES.preSalesLeads,        element: <PreSalesLeadsPage /> },
              { path: ROUTES.preSalesLeadsNew,     element: <CreateLeadPage /> },
              { path: ROUTES.preSalesPipeline,     element: <PreSalesPipelinePage /> },
              /*
                Retired by the V2 prototype, which has neither screen. An
                interaction is logged in the drawer that opens from a lead, and
                a follow-up is worked on the day it is due from the dashboard
                queue — so both standalone lists asked the rep to navigate to
                work they had already been shown.

                Redirects rather than deletions: the paths were in the nav for
                months and will be bookmarked. Pipeline is where both now live.
              */
              { path: ROUTES.preSalesFollowups,    element: <RedirectPreservingSearch to={ROUTES.preSalesPipeline} /> },
              { path: ROUTES.preSalesInteractions, element: <RedirectPreservingSearch to={ROUTES.preSalesPipeline} /> },
              { path: ROUTES.preSalesProposals,    element: <PreSalesProposalsPage /> },
              { path: ROUTES.preSalesWhatsapp,     element: <WhatsAppGroupsPage /> },
              { path: ROUTES.preSalesIntakeReview, element: <IntakeReviewPage /> },
              { path: ROUTES.preSalesReferrals,    element: <PreSalesReferralsPage /> },
              { path: ROUTES.postSales,            element: <RedirectPreservingSearch to={ROUTES.home} /> },
              { path: ROUTES.postSalesClients,     element: <PostSalesClientsPage /> },
              { path: ROUTES.postSalesClientsNew,  element: <CreateClientPage /> },
              { path: ROUTES.postSalesDelivery,    element: <PostSalesDeliveryPage /> },
              { path: ROUTES.postSalesDeliveryProject, element: <ProjectDetailPage /> },
              { path: ROUTES.postSalesFollowups,   element: <RedirectPreservingSearch to={ROUTES.postSalesClients} /> },
              { path: ROUTES.postSalesClientProfile, element: <PostSalesClientProfilePage /> },
              { path: ROUTES.postSalesCompanies,   element: <PostSalesCompaniesPage /> },
              { path: ROUTES.postSalesCompany,     element: <CompanyWorkspacePage /> },
              { path: ROUTES.postSalesClient,      element: <ClientDeepDivePage /> },
              // ── Astrology workspace (spec §1.2). STAFF-level: the astrologer
              // is a STAFF-audience role; screen visibility is the object
              // registry's job (astrology.* slugs), route data access is the
              // backend's (@Permissions on /astrology/*).
              { path: ROUTES.astrologyKundali,         element: <KundaliPage /> },
              { path: ROUTES.astrologyKundaliContact,  element: <KundaliPage /> },
              { path: ROUTES.astrologyWorkbench,       element: <KundaliWorkbenchPage /> },
              { path: ROUTES.astrologyEventChart,      element: <EventKundaliPage /> },
              // ── HR workspace, phase 1. Screen visibility is the object
              // registry's (hr.* slugs); data access is @Permissions on /hr/*.
              { path: ROUTES.hr,          element: <RedirectPreservingSearch to={ROUTES.hrDirectory} /> },
              { path: ROUTES.hrDirectory, element: <StaffDirectoryPage /> },
              { path: ROUTES.hrEmployee,  element: <EmployeeProfilePage /> },
              { path: ROUTES.hrSettings,  element: <HrSettingsPage /> },
              { path: ROUTES.hrHiring,     element: <HiringPage /> },
              { path: ROUTES.hrOnboarding, element: <OnboardingPage /> },
              { path: ROUTES.hrAttendance, element: <AttendancePage /> },
              { path: ROUTES.hrShifts,     element: <ShiftsPage /> },
              { path: ROUTES.hrApprovals,  element: <ApprovalsPage /> },
              { path: ROUTES.hrLeave,      element: <LeavePage /> },
              { path: ROUTES.hrReconciliation, element: <ReconciliationPage /> },
              { path: ROUTES.hrPayroll, element: <PayrollPage /> },
              { path: ROUTES.hrPulse, element: <PulsePage /> },
              { path: ROUTES.hrCalendar, element: <HrCalendarPage /> },
              { path: ROUTES.hrSeparation, element: <HrSeparationPage /> },
              { path: ROUTES.adminAttachments, element: <TaskAttachmentsPage /> },
              { path: ROUTES.adminTasks,     element: <TasksPage /> },
              { path: ROUTES.adminTaskTypes, element: <TaskTypesPage /> },
              { path: ROUTES.adminPerformance, element: <PerformancePage /> },
              { path: ROUTES.postSalesRenewals, element: <RenewalsPage /> },
              { path: ROUTES.postSalesNewInterests, element: <NewInterestsPage /> },
              { path: ROUTES.postSalesCommunity, element: <CommunityAdminPage /> },
              { path: ROUTES.adminBizcards,     element: <BizCardsPage /> },
              { path: ROUTES.postSalesBizcards, element: <RedirectPreservingSearch to={ROUTES.adminBizcards} /> },
              { path: ROUTES.astrologyBizcalendars,    element: <BizCalendarsPage /> },
              { path: ROUTES.astrologyReview,          element: <BizCalendarReviewPage /> },
              { path: ROUTES.astrologyReviewDetail,    element: <BizCalendarReviewDetailPage /> },
              { path: ROUTES.astrologyReports,         element: <ReportsPage /> },
              { path: ROUTES.astrologyAstroUpdates,    element: <ComposerPage /> },
              { path: ROUTES.astrologyRemedyCatalogue, element: <RemedyCatalogPage /> },
              { path: ROUTES.astrologyNumerology,      element: <AstrologyNumerologyPage /> },
              { path: ROUTES.astrologyRules,           element: <RulesReferencePage /> },
              // Old paths redirect into the workspace for a release (spec §1.2).
              { path: ROUTES.composer,      element: <RedirectPreservingSearch to={ROUTES.astrologyAstroUpdates} /> },
              { path: ROUTES.remedyCatalog, element: <RedirectPreservingSearch to={ROUTES.astrologyRemedyCatalogue} /> },
              { path: ROUTES.reports,       element: <RedirectPreservingSearch to={ROUTES.astrologyReports} /> },
              { path: ROUTES.bizcalendarReview,       element: <RedirectPreservingSearch to={ROUTES.astrologyReview} /> },
              { path: ROUTES.bizcalendarReviewDetail, element: <RedirectReviewDetail /> },
              // The old signup-approval screen is retired (management decision
              // #22): Intake Review decides signups, for everyone who could.
              { path: ROUTES.approval, element: <RedirectPreservingSearch to={ROUTES.preSalesIntakeReview} /> },
              {
                // Gated by the object registry (admin.* grants), NOT the legacy
                // User.role string — see ObjectGuard for the tenant-admin outage.
                element: <ObjectGuard />,
                children: [
                  { path: ROUTES.pricing,              element: <PricingPage /> },
                  { path: ROUTES.items,                element: <ItemsPage /> },
                  { path: ROUTES.packageApproval,      element: <PackagePaymentPage /> },
                  { path: ROUTES.lov,       element: <LovPage /> },
                  { path: ROUTES.lovNew,    element: <LovFormPage /> },
                  { path: ROUTES.lovEdit,   element: <LovFormPage /> },
                  { path: ROUTES.lovValues, element: <LovValuesPage /> },
                  // Settings — the prototype's seven-section screen. The
                  // section is a URL segment so it can be linked and so Back
                  // returns to the section, not out of Settings entirely.
                  { path: ROUTES.settings,             element: <SettingsPage /> },
                  { path: ROUTES.settingsSection,      element: <SettingsPage /> },
                  // Follow-up Settings was its own screen until 2026-08-11.
                  // It is Settings section 1 now; the old path redirects so
                  // bookmarks and anything still linking to it keep working.
                  {
                    path: ROUTES.followupSettings,
                    element: <Navigate to={ROUTES.settingsFollowUpRules} replace />,
                  },
                  { path: ROUTES.payments,             element: <PaymentsPage /> },
                  // Composer, Remedy Catalog, Reports and BizCalendar Review
                  // moved to the Astrology workspace (spec §1.1) — their old
                  // paths redirect there from the STAFF-level block above.
                  { path: ROUTES.access,               element: <AccessControlPage /> },
                  // Old bookmarks and any link still pointing at the retired
                  // Role Permissions screen land on the console instead of 404.
                  { path: ROUTES.rolePermissions,      element: <Navigate to={ROUTES.access} replace /> },
                  { path: ROUTES.auditLog,             element: <AuditLogPage /> },
                  { path: ROUTES.jobHealth,            element: <JobHealthPage /> },
                  // Report limits admin — our addition.
                  { path: ROUTES.reportLimits,         element: <ReportLimitsPage /> },
                  // Team & Security overview — the prototype's actual page
                  // name; Access Control/Audit Log/Report Limits above are
                  // its other three tabs (see TeamSecurityTabs.tsx).
                  { path: ROUTES.teamSecurity,         element: <TeamSecurityOverviewPage /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // -------- Client portal (anyone with a client record — A9) --------
  {
    element: <AuthGuard />,
    children: [
      {
        element: <PortalGuard />,
        children: [
          {
            element: <ClientPortalLayout />,
            children: [
              { path: ROUTES.portal,              element: <ClientPortalPage /> },
              { path: ROUTES.portalReports,       element: <ClientPortalReportPage /> },
              { path: ROUTES.portalProfile,       element: <ClientPortalProfilePage /> },
              { path: ROUTES.portalCommunity,     element: <ClientPortalCommunityPage /> },
              { path: ROUTES.portalBizcalendar,   element: <ClientPortalBizCalendarPage /> },
              { path: ROUTES.portalRemedies,      element: <ClientPortalRemediesPage /> },
              { path: ROUTES.portalDasha,         element: <ClientPortalDashaPage /> },
              { path: ROUTES.portalPayments,      element: <ClientPortalPaymentPage /> },
              { path: ROUTES.portalAstroUpdates,  element: <ClientPortalAstroUpdatesPage /> },
              // Our additions — Refer & Earn and Numerology Center.
              { path: ROUTES.portalReferAndEarn,  element: <ClientPortalReferAndEarnPage /> },
              { path: ROUTES.portalNumerology,    element: <ClientPortalNumerologyCenterPage /> },
              { path: ROUTES.portalPackages,      element: <ClientPortalPackagesPage /> },
              { path: ROUTES.portalPlans,         element: <ClientPortalPlansPage /> },
              { path: ROUTES.portalLedger,        element: <ClientPortalLedgerPage /> },
              { path: ROUTES.portalProjects,      element: <ClientPortalProjectsPage /> },
              { path: ROUTES.portalBizcard,       element: <ClientPortalBizCardPage /> },
              { path: ROUTES.portalCommunityApply, element: <ClientPortalCommunityApplyPage /> },
              { path: ROUTES.portalCommunityChapter, element: <ClientPortalChapterPage /> },
              { path: ROUTES.portalCommunityMeetings, element: <ClientPortalMeetingsPage /> },
              { path: ROUTES.portalCommunityBusiness, element: <ClientPortalBusinessPage /> },
              { path: ROUTES.portalCommunityFeed, element: <ClientPortalFeedPage /> },
              { path: ROUTES.portalCommunityBoard, element: <ClientPortalBoardPage /> },
              { path: ROUTES.portalCommunityWorkshops, element: <ClientPortalWorkshopsPage /> },
              { path: ROUTES.portalWorkshopCertificate, element: <WorkshopCertificatePage /> },
              { path: ROUTES.portalCommunityAnalytics, element: <ClientPortalAnalyticsPage /> },
              { path: ROUTES.portalAccountSettings, element: <ClientAccountSettingsPage /> },
              { path: ROUTES.portalEraseData,       element: <ClientEraseDataPage /> },
            ],
          },
        ],
      },
    ],
  },

  {
    path: "*",
    element: <NotFoundPage />,
  },
]);
